import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';
import { Plus as AddIcon, Ban as RetireIcon, Gavel as GavelIcon, ArrowUpCircle as NewVersionIcon } from 'lucide-react';
import { Button as UiButton, Chip, CoordioDropdown, Input, ShellHeader, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { EmptyState, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { memoryModuleService } from '../services/memoryModuleService';
import { useNotificationStore } from '../store/notificationStore';
import {
  AnonymizationState,
  LifecycleStatus,
  MemoryImpactTier,
  MemoryPipelineState,
  RuleAuthoringSource,
  SCOPE_DIMENSIONS,
  impactTierLabel,
  lifecycleStatusLabel,
  pipelineStateLabel,
  type AgentMemoryDefinitionDto,
  type DecisionReconstructionDto,
  type MemoryCandidateDto,
  type ProceduralRuleDto,
  type RuleEfficacyDto,
} from '../types/memoryModule';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { MemoryInjectionPreview } from '../components/memory/MemoryInjectionPreview';
import { ROUTES } from '../utils/routes';

interface RuleFormState {
  ruleText: string;
  triggerJson: string;
  rationaleText: string;
  impactTier: number;
  declaredScope: Record<string, string>;
  reason: string;
}

const EMPTY_RULE_FORM: RuleFormState = {
  ruleText: '',
  triggerJson: '',
  rationaleText: '',
  impactTier: MemoryImpactTier.Operational,
  declaredScope: {},
  reason: '',
};

const statusChip = (rule: ProceduralRuleDto) => {
  if (rule.status === LifecycleStatus.Draft) {
    return <Chip variant="outlined" label="Awaiting approval" />;
  }
  if (rule.status === LifecycleStatus.Active) {
    return <Chip label="Active" />;
  }
  return <Chip variant="outlined" label={lifecycleStatusLabel(rule.status)} />;
};

/**
 * SME procedural rule authoring and review (Memory spec §9.14, §10A.4). Rules land as Draft and
 * are approved on the existing safety-gate approval queue — there is deliberately no approve
 * button here (owner decision 12: one inbox).
 */
export const MemoryRulesPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [definitions, setDefinitions] = useState<AgentMemoryDefinitionDto[]>([]);
  const [selectedDef, setSelectedDef] = useState<AgentMemoryDefinitionDto | null>(null);
  const [rules, setRules] = useState<ProceduralRuleDto[]>([]);
  const [efficacyByFamily, setEfficacyByFamily] = useState<Record<string, RuleEfficacyDto[]>>({});
  const [candidates, setCandidates] = useState<MemoryCandidateDto[]>([]);
  const [tab, setTab] = useState<'rules' | 'candidates' | 'try' | 'decisions'>('rules');
  const [decisionSessionId, setDecisionSessionId] = useState('');
  const [decisionAsOf, setDecisionAsOf] = useState('');
  const [decision, setDecision] = useState<DecisionReconstructionDto | null>(null);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Authoring dialog: null = closed; otherwise a new rule or a new version of `family`.
  const [dialog, setDialog] = useState<{ family: ProceduralRuleDto | null } | null>(null);
  const [ruleForm, setRuleForm] = useState<RuleFormState>(EMPTY_RULE_FORM);

  // Retire dialog
  const [retireTarget, setRetireTarget] = useState<ProceduralRuleDto | null>(null);
  const [retireReason, setRetireReason] = useState('');

  useEffect(() => {
    memoryModuleService
      .listDefinitions()
      .then((defs) => {
        setDefinitions(defs);
        if (defs.length > 0) setSelectedDef(defs[0]);
        else setLoading(false);
      })
      .catch((err: any) => {
        setError(err.response?.data?.message || 'Failed to load memory definitions');
        setLoading(false);
      });
  }, []);

  const loadRules = useCallback(async (definitionId: string) => {
    setLoading(true);
    setError(null);
    try {
      const [ruleList, candidateList] = await Promise.all([
        memoryModuleService.listRules(definitionId),
        // All states: the write path lands candidates as New, then Anonymized/Quarantined/etc.
        memoryModuleService.listCandidates({ state: '', definitionId }),
      ]);
      setRules(ruleList);
      setCandidates(candidateList);
      const families = [...new Set(ruleList.map((r) => r.ruleFamilyId))];
      const measured = await Promise.all(
        families.map(async (familyId) => {
          try {
            const rows = await memoryModuleService.getRuleEfficacy(familyId, definitionId);
            return [familyId, rows] as const;
          } catch {
            return [familyId, [] as RuleEfficacyDto[]] as const;
          }
        })
      );
      setEfficacyByFamily(Object.fromEntries(measured));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDef) loadRules(selectedDef.agentMemoryDefinitionId);
  }, [selectedDef, loadRules]);

  const scopeDimensionsInUse = useMemo(() => {
    if (!selectedDef) return [];
    const configured = new Set(
      selectedDef.scopeSlots.filter((s) => s.dimensionKey).map((s) => s.dimensionKey!)
    );
    return SCOPE_DIMENSIONS.filter((d) => configured.has(d.key));
  }, [selectedDef]);

  const openNewRule = () => {
    setRuleForm(EMPTY_RULE_FORM);
    setDialog({ family: null });
  };

  const openNewVersion = (rule: ProceduralRuleDto) => {
    setRuleForm({
      ruleText: rule.ruleText,
      triggerJson: rule.triggerJson === '{}' ? '' : rule.triggerJson,
      rationaleText: rule.rationaleText ?? '',
      impactTier: rule.impactTier,
      declaredScope: {},
      reason: '',
    });
    setDialog({ family: rule });
  };

  const handleSubmitRule = async () => {
    if (!selectedDef) return;
    if (!ruleForm.ruleText.trim()) {
      addNotification('Rule text is required', 'error');
      return;
    }
    if (ruleForm.triggerJson.trim()) {
      try {
        JSON.parse(ruleForm.triggerJson);
      } catch {
        addNotification('Trigger must be valid JSON', 'error');
        return;
      }
    }

    const declaredScope = Object.fromEntries(
      Object.entries(ruleForm.declaredScope).filter(([, v]) => v.trim() !== '')
    );

    setBusy(true);
    try {
      const payload = {
        agentMemoryDefinitionId: selectedDef.agentMemoryDefinitionId,
        ruleText: ruleForm.ruleText.trim(),
        triggerJson: ruleForm.triggerJson.trim() || null,
        rationaleText: ruleForm.rationaleText.trim() || null,
        impactTier: ruleForm.impactTier,
        declaredScope: Object.keys(declaredScope).length > 0 ? declaredScope : null,
        reason: ruleForm.reason.trim() || null,
      };
      if (dialog?.family) {
        await memoryModuleService.createRuleVersion(dialog.family.ruleFamilyId, payload);
        addNotification(
          'New version submitted for approval — the incumbent keeps injecting until it is approved',
          'success'
        );
      } else {
        await memoryModuleService.createRule(payload);
        addNotification('Rule submitted — approval happens on the Approval Queue', 'success');
      }
      setDialog(null);
      await loadRules(selectedDef.agentMemoryDefinitionId);
    } catch (err: any) {
      const data = err.response?.data;
      const details = Array.isArray(data?.errors) ? `: ${data.errors.join('; ')}` : '';
      addNotification((data?.message || 'Failed to submit the rule') + details, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRetire = async () => {
    if (!retireTarget || !selectedDef) return;
    if (!retireReason.trim()) {
      addNotification('A retirement reason is required', 'error');
      return;
    }
    setBusy(true);
    try {
      await memoryModuleService.retireRule(
        retireTarget.ruleFamilyId,
        selectedDef.agentMemoryDefinitionId,
        retireReason.trim()
      );
      addNotification('Rule retired', 'success');
      setRetireTarget(null);
      setRetireReason('');
      await loadRules(selectedDef.agentMemoryDefinitionId);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to retire the rule', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleReconstruct = async () => {
    if (!decisionSessionId.trim()) {
      addNotification('A session id is required', 'error');
      return;
    }
    setDecisionBusy(true);
    try {
      const asOf = decisionAsOf.trim()
        ? new Date(decisionAsOf).toISOString()
        : undefined;
      const result = await memoryModuleService.getDecisionReconstruction(
        decisionSessionId.trim(),
        asOf
      );
      setDecision(result);
    } catch (err: any) {
      setDecision(null);
      addNotification(err.response?.data?.message || 'Failed to reconstruct decisions', 'error');
    } finally {
      setDecisionBusy(false);
    }
  };

  const ruleColumns: DataTableColumn<ProceduralRuleDto>[] = [
    {
      id: 'ruleText',
      label: 'Rule',
      minWidth: 340,
      render: (r) => (
        <div>
          <p className="text-sm">{r.ruleText}</p>
          {r.rationaleText && (
            <span className="text-xs text-[var(--color-text-secondary)]">
              Rationale: {r.rationaleText}
            </span>
          )}
        </div>
      ),
    },
    { id: 'version', label: 'Version', minWidth: 80, align: 'right', render: (r) => `v${r.version}` },
    { id: 'status', label: 'Status', minWidth: 140, render: (r) => statusChip(r) },
    {
      id: 'impactTier',
      label: 'Impact',
      minWidth: 110,
      render: (r) => (
        <Chip
          variant="outlined"
          label={impactTierLabel(r.impactTier)}
        />
      ),
    },
    {
      id: 'authoringSource',
      label: 'Source',
      minWidth: 100,
      render: (r) => (r.authoringSource === RuleAuthoringSource.SmeAuthored ? 'SME' : 'Reflection'),
    },
    {
      id: 'outcome',
      label: 'Success / Failure',
      minWidth: 130,
      align: 'right',
      render: (r) => `${r.successCount} / ${r.failureCount}`,
    },
    {
      id: 'lift',
      label: 'Lift',
      minWidth: 160,
      render: (r) => {
        const row = (efficacyByFamily[r.ruleFamilyId] ?? []).find((e) => e.version === r.version);
        if (!row) {
          return (
            <span className="text-xs text-[var(--color-text-secondary)]">
              —
            </span>
          );
        }
        const pct = `${(row.weightedLift * 100).toFixed(1)}%`;
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Chip
                    variant="outlined"
                    label={`${pct} (n=${row.injectedRuns})`}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {`CI ${(row.liftCiLow * 100).toFixed(1)}% to ${(row.liftCiHigh * 100).toFixed(1)}% · ${row.injectedRuns} injected / ${row.comparisonRuns} comparison`}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      id: 'createdAtUtc',
      label: 'Created',
      minWidth: 150,
      render: (r) => new Date(r.createdAtUtc).toLocaleString(),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 110,
      align: 'right',
      render: (r) => (
        <div className="flex gap-0.5 justify-end">
          {r.status === LifecycleStatus.Draft && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <UiButton variant="ghost" size="icon" onClick={() => navigate(ROUTES.APPROVAL_QUEUE)}>
                    <GavelIcon size={16} />
                  </UiButton>
                </TooltipTrigger>
                <TooltipContent>Review on the Approval Queue</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {r.status === LifecycleStatus.Active && (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <UiButton variant="ghost" size="icon" onClick={() => openNewVersion(r)}>
                      <NewVersionIcon size={16} />
                    </UiButton>
                  </TooltipTrigger>
                  <TooltipContent>Author a new version</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <UiButton variant="ghost" size="icon" onClick={() => setRetireTarget(r)}>
                      <RetireIcon size={16} />
                    </UiButton>
                  </TooltipTrigger>
                  <TooltipContent>Retire</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}
        </div>
      ),
    },
  ];

  const candidateColumns: DataTableColumn<MemoryCandidateDto>[] = [
    {
      id: 'content',
      label: 'Candidate',
      minWidth: 360,
      render: (c) => (
        <div>
          <p className="text-sm whitespace-pre-line">{c.content}</p>
          {c.samplingStratum && (
            <span className="text-xs text-[var(--color-text-secondary)]">
              Admitted by: {c.samplingStratum}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'pipelineState',
      label: 'State',
      minWidth: 130,
      render: (c) => (
        <div className="flex gap-0.5 flex-wrap">
          <Chip
            variant={c.pipelineState === MemoryPipelineState.Promoted ? 'filled' : 'outlined'}
            label={pipelineStateLabel(c.pipelineState)}
          />
          {c.anonymizationState === AnonymizationState.Quarantined && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span><Chip label="Quarantined" /></span>
                </TooltipTrigger>
                <TooltipContent>{c.verificationFailures ? `Failed checks: ${c.verificationFailures}` : 'Pass 3 verification failed'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
    {
      id: 'session',
      label: 'Session',
      minWidth: 120,
      render: (c) =>
        c.agentSessionId ? (
          <button className="text-sm text-[var(--color-accent-primary)] hover:underline" onClick={() => navigate(`/sessions/${c.agentSessionId}`)}>
            {c.agentSessionId.slice(0, 8)}…
          </button>
        ) : (
          '—'
        ),
    },
    {
      id: 'impactTier',
      label: 'Impact',
      minWidth: 110,
      render: (c) => impactTierLabel(c.impactTier),
    },
    {
      id: 'reviewSlaUtc',
      label: 'Review SLA',
      minWidth: 150,
      render: (c) => (c.reviewSlaUtc ? new Date(c.reviewSlaUtc).toLocaleString() : '—'),
    },
    {
      id: 'createdAtUtc',
      label: 'Created',
      minWidth: 150,
      render: (c) => new Date(c.createdAtUtc).toLocaleString(),
    },
  ];

  return (
    <>
      <ShellHeader
        data-testid="memory-rules-header"
        title="Memory Rules"
        description="SME-authored procedural rules per Agent Memory Definition. New rules and versions land as Draft and are approved on the Approval Queue — never here."
        actions={
          <>
            <UiButton data-testid="memory-rules-approval-queue-btn" variant="outline" onClick={() => navigate(ROUTES.APPROVAL_QUEUE)}>
              Approval Queue
            </UiButton>
            {selectedDef && (
              <UiButton data-testid="memory-rules-new-rule-btn" variant="default" onClick={openNewRule}>
                <AddIcon /> New Rule
              </UiButton>
            )}
          </>
        }
      />

      <PageContainer data-testid="memory-rules-form-page">
        {error && (
          <InlineErrorPanel
            data-testid="memory-rules-error-panel"
            message={error}
            action={<UiButton variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</UiButton>}
          />
        )}

        {loading && definitions.length === 0 ? (
          <LoadingSkeleton data-testid="memory-rules-loading" rows={6} />
        ) : !loading && definitions.length === 0 ? (
          <EmptyState
            title="No memory definitions"
            description="Rules belong to an Agent Memory Definition. Create a definition first."
            action={<UiButton variant="default" size="sm" onClick={() => navigate(ROUTES.MEMORY_DEFINITIONS)}>Memory Definitions</UiButton>}
          />
        ) : (
          <>
            {definitions.length > 0 && (
              <div className="mb-3 max-w-[480px]">
                <label className="mb-1 block text-sm font-medium">Agent Memory Definition</label>
                <CoordioDropdown
                  options={definitions.map((d) => ({
                    value: d.agentMemoryDefinitionId,
                    label: `${d.displayName} (${d.agentKey})`,
                  }))}
                  value={selectedDef?.agentMemoryDefinitionId ?? null}
                  onChange={(val) => {
                    const next = definitions.find((d) => d.agentMemoryDefinitionId === val);
                    if (next) setSelectedDef(next);
                  }}
                  clearable={false}
                  data-testid="memory-rules-definition-selector"
                />
              </div>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
              <Tab data-testid="memory-rules-tab-rules" value="rules" label={`Rules (${rules.length})`} />
              <Tab data-testid="memory-rules-tab-candidates" value="candidates" label={`Pending candidates (${candidates.length})`} />
              <Tab data-testid="memory-rules-tab-try" value="try" label="Try injection" />
              <Tab data-testid="memory-rules-tab-decisions" value="decisions" label="Decisions" />
            </Tabs>

            {tab === 'rules' && (
              <DataTable
                data-testid="memory-rules-panel-rules"
                columns={ruleColumns}
                rows={rules}
                loading={loading}
                emptyMessage="No rules for this definition yet — author the first one."
              />
            )}
            {tab === 'candidates' && (
              <DataTable
                data-testid="memory-rules-panel-candidates"
                columns={candidateColumns}
                rows={candidates}
                loading={loading}
                emptyMessage="No candidates yet — complete a scoped session and let the extraction worker run."
              />
            )}
            {tab === 'try' && selectedDef && <MemoryInjectionPreview definition={selectedDef} />}
            {tab === 'decisions' && (
              <div data-testid="memory-rules-panel-decisions">
                <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                  Reconstruct what was injected into a session and the rule text that was live at that
                  instant. Superseded versions appear when as-of falls inside their validity window.
                </p>
                <div className="flex flex-wrap gap-2 mb-3 items-center">
                  <div className="min-w-[320px]">
                    <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Session id</label>
                    <Input
                      value={decisionSessionId}
                      onChange={(e) => setDecisionSessionId(e.target.value)}
                      data-testid="memory-rules-decisionSessionId"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">As of (UTC)</label>
                    <Input
                      type="datetime-local"
                      value={decisionAsOf}
                      onChange={(e) => setDecisionAsOf(e.target.value)}
                      data-testid="memory-rules-decisionAsOf"
                    />
                  </div>
                  <button
                    className="rounded-lg bg-[var(--color-accent-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                    data-testid="memory-rules-reconstruct-btn"
                    onClick={handleReconstruct}
                    disabled={decisionBusy}
                  >
                    {decisionBusy ? 'Reconstructing…' : 'Reconstruct'}
                  </button>
                </div>
                {decision && (
                  <div>
                    <span className="text-xs text-[var(--color-text-secondary)] block mb-1">
                      asOf {new Date(decision.asOfUtc).toLocaleString()} — {decision.injections.length} injection
                      {decision.injections.length === 1 ? '' : 's'}
                    </span>
                    {decision.rulesAtAsOf.length === 0 && decision.injections.length === 0 && (
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        No injection audit or live rules for this session at that instant.
                      </p>
                    )}
                    {decision.rulesAtAsOf.map((rule) => (
                      <div
                        key={`${rule.ruleFamilyId}-${rule.version}`}
                        className="py-1.5 border-b border-[var(--color-border-default)]"
                      >
                        <div className="flex gap-1 mb-0.5 flex-wrap">
                          {rule.wasInjected && <Chip label="Injected" />}
                          <Chip variant="outlined" label={`v${rule.version}`} />
                          <Chip variant="outlined" label={rule.status} />
                          <Chip variant="outlined" label={rule.recordState} />
                        </div>
                        <p className="text-sm">{rule.ruleText}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </PageContainer>

      {/* Authoring dialog: new rule or new version */}
      <Dialog data-testid="memory-rules-authoring-dialog" open={dialog !== null} onClose={() => setDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialog?.family
            ? `New version of rule family (currently v${dialog.family.version})`
            : 'New procedural rule'}
        </DialogTitle>
        <DialogContent>
          <div className="grid grid-cols-1 gap-3 mt-0">
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Rule text (injected into agent instructions verbatim) *</label>
              <Textarea
                className="w-full"
                required
                rows={3}
                value={ruleForm.ruleText}
                onChange={(e) => setRuleForm((f) => ({ ...f, ruleText: e.target.value }))}
                data-testid="memory-rules-ruleText"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Rationale (shown to reviewers, never injected)</label>
              <Textarea
                className="w-full"
                rows={2}
                value={ruleForm.rationaleText}
                onChange={(e) => setRuleForm((f) => ({ ...f, rationaleText: e.target.value }))}
                data-testid="memory-rules-rationaleText"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Impact tier</label>
                <CoordioDropdown
                  options={[
                    { value: String(MemoryImpactTier.Operational), label: 'Operational' },
                    { value: String(MemoryImpactTier.Financial), label: 'Financial' },
                    { value: String(MemoryImpactTier.Clinical), label: 'Clinical' },
                  ]}
                  value={String(ruleForm.impactTier)}
                  onChange={(val) => setRuleForm((f) => ({ ...f, impactTier: Number(val) }))}
                  data-testid="memory-rules-impactTier"
                />
                <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                  {dialog?.family
                    ? 'A new version may escalate the tier but never de-escalate it'
                    : 'Determines the review bar; Clinical is the highest'}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Reason for change</label>
                <Input
                  className="w-full"
                  value={ruleForm.reason}
                  onChange={(e) => setRuleForm((f) => ({ ...f, reason: e.target.value }))}
                  data-testid="memory-rules-reason"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Trigger JSON (optional predicate; empty = always eligible)</label>
              <Textarea
                className="w-full font-mono text-[13px]"
                rows={2}
                value={ruleForm.triggerJson}
                onChange={(e) => setRuleForm((f) => ({ ...f, triggerJson: e.target.value }))}
                data-testid="memory-rules-triggerJson"
              />
            </div>
            {scopeDimensionsInUse.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-1">
                  Declared scope (optional)
                </p>
                <span className="text-xs text-[var(--color-text-secondary)] block mb-2">
                  Narrow the rule to specific dimension values. Leave blank to apply across the
                  definition. The rule is shared knowledge — it is never user-scoped.
                </span>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {scopeDimensionsInUse.map((d) => (
                    <div key={d.key}>
                      <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">{d.key}</label>
                      <Input
                        className="w-full"
                        value={ruleForm.declaredScope[d.key] ?? ''}
                        onChange={(e) =>
                          setRuleForm((f) => ({
                            ...f,
                            declaredScope: { ...f.declaredScope, [d.key]: e.target.value },
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <button className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-[var(--color-bg-subtle)]" data-testid="cancel-memory-rules-btn" onClick={() => setDialog(null)}>Cancel</button>
          <button className="rounded-lg bg-[var(--color-accent-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50" data-testid="save-memory-rules-btn" onClick={handleSubmitRule} disabled={busy}>
            Submit for approval
          </button>
        </DialogActions>
      </Dialog>

      {/* Retire dialog */}
      <Dialog data-testid="memory-rules-retire-dialog" open={retireTarget !== null} onClose={() => setRetireTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Retire rule</DialogTitle>
        <DialogContent>
          <p className="text-sm text-[var(--color-text-secondary)] mb-3">
            The rule stops injecting immediately. The row persists for the bitemporal audit trail.
          </p>
          <p className="text-sm mb-3 italic">
            "{retireTarget?.ruleText}"
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Retirement reason *</label>
            <Input
              className="w-full"
              required
              value={retireReason}
              onChange={(e) => setRetireReason(e.target.value)}
              data-testid="memory-rules-retireReason"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <button className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-[var(--color-bg-subtle)]" data-testid="cancel-memory-rules-retire-btn" onClick={() => setRetireTarget(null)}>Cancel</button>
          <button className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50" data-testid="memory-rules-retire-btn" onClick={handleRetire} disabled={busy}>
            Retire
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MemoryRulesPage;
